import React, { useEffect, useRef, useState } from "react";
import "../ComponentsCss/BackgroundFooter.css";

const BackgroundFooter = ({ isSidebarCollapsed, userData }) => { // Add userData prop
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  
  // Trigger button ref (close hone par yahin focus wapas aayega)
  const helpBtnRef = useRef(null);
  // Modal container ref (focus & key handling)
  const modalRef = useRef(null);

  const getUserRegion = () => {
    return sessionStorage.getItem("userRegion") || "GB"; // Default to UK
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("");

    try {
      // Combine form data with user data automatically - user doesn't see this
      const submissionData = {
        ...formData,
        // Hidden user info from Cognito - automatically included
        userInfo: {
          cognitoUsername: userData?.["cognito:username"] || null,
          cognitoName: userData?.name || userData?.given_name || null,
          cognitoEmail: userData?.email || null,
          cognitoPhone: userData?.phone_number || null,
          userRegion: getUserRegion(),
          submissionTime: new Date().toISOString(),
        }
      };

      const response = await fetch(
        "https://himmv4ad95.execute-api.ap-southeast-2.amazonaws.com/prod/api/support/submit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Region: getUserRegion(),
          },
          body: JSON.stringify(submissionData),
        }
      );

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
        setTimeout(() => {
          closeSupportForm();
        }, 2000);
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeSupportForm = () => {
    setShowSupportForm(false);
    setSubmitStatus("");
    // Reset form completely
    setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    // next tick par focus restore (DOM update ke baad)
    setTimeout(() => helpBtnRef.current?.focus(), 0);
  };

  // 🔒 Focus trap + Esc + body scroll lock (keeping existing logic)
  useEffect(() => {
    if (!showSupportForm) return;

    const modalEl = modalRef.current;
    if (!modalEl) return;

    // Body scroll lock
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus modal container for SR + keyboard
    modalEl.setAttribute("tabindex", "-1");
    modalEl.focus();

    // Collect focusable elements inside modal
    const selectors =
      'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

    const isVisible = (el) => {
      // hidden via css or not in layout
      const style = window.getComputedStyle(el);
      return (
        style.visibility !== "hidden" &&
        style.display !== "none" &&
        el.offsetParent !== null
      );
    };

    const getFocusables = () =>
      Array.from(modalEl.querySelectorAll(selectors)).filter(isVisible);

    let focusables = getFocusables();
    const first = () => (focusables = getFocusables(), focusables[0]);
    const last = () => (focusables = getFocusables(), focusables[focusables.length - 1]);

    // Initial focus: first focusable if exists
    first()?.focus?.();

    const onKeyDown = (e) => {
      // ESC to close
      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        e.stopPropagation();
        closeSupportForm();
        return;
      }

      // TAB trap
      if (e.key === "Tab") {
        focusables = getFocusables();
        if (focusables.length === 0) {
          e.preventDefault();
          return;
        }
        const active = document.activeElement;

        // Shift+Tab on first -> jump to last
        if (e.shiftKey && active === first()) {
          e.preventDefault();
          last()?.focus?.();
          return;
        }
        // Tab on last -> jump to first
        if (!e.shiftKey && active === last()) {
          e.preventDefault();
          first()?.focus?.();
          return;
        }
      }
    };

    document.addEventListener("keydown", onKeyDown, { capture: true });

    // Cleanup on close/unmount
    return () => {
      document.removeEventListener("keydown", onKeyDown, { capture: true });
      document.body.style.overflow = prevOverflow;
    };
  }, [showSupportForm]);

  return (
    <>
      <footer className={`footer ${isSidebarCollapsed ? "collapsed" : "expanded"}`}>
        <p className="footertext">
          SUSAN
          <span
            style={{
              fontSize: "1.1rem",
              verticalAlign: "super",
              fontWeight: 300,
              marginLeft: 0,
              position: "relative",
              top: "3px",
            }}
          >
            ®
          </span>{" "}
          is a registered/trademarked product of{" "}
          <a href="https://srql.com/">ServQual Limited</a>, UK.
        </p>

        {/* 👉 IF–ELSE only on Get Help */}
        {isSidebarCollapsed ? (
          <button
            ref={helpBtnRef}
            onClick={() => setShowSupportForm(true)}
            id="Help"
            className="gethelp gethelp-closed"
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Get Help
          </button>
        ) : (
          <button
            ref={helpBtnRef}
            onClick={() => setShowSupportForm(true)}
            id="Help"
            className="gethelp gethelp-open"
            style={{
              background: "none",
              border: "none",
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Get Help
          </button>
        )}
      </footer>

      {showSupportForm && (
        <div
          className="support-modal-overlay"
          onClick={closeSupportForm}
          aria-hidden="true"
        >
          <div
            className="support-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="support-modal-title"
            onClick={(e) => e.stopPropagation()}
            ref={modalRef}
          >
            <div className="support-modal-header">
              <h3 id="support-modal-title">Contact Support</h3>
              <button
                className="close-button"
                onClick={closeSupportForm}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="support-form">
              {/* Regular form fields - no auto-fill visible to user */}
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="Enter your phone (optional)"
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Subject/New Feature request *</label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  placeholder="Brief description of your request"
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="5"
                  required
                  placeholder="Please provide detailed information about your request or issue..."
                ></textarea>
              </div>

              <div className="form-actions">
                <button type="button" onClick={closeSupportForm} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={isSubmitting} className="submit-btn">
                  {isSubmitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>

              {submitStatus === "success" && (
                <div className="status-message success">
                  Thank you! Your support request has been submitted successfully.
                </div>
              )}

              {submitStatus === "error" && (
                <div className="status-message error">
                  Sorry, there was an error submitting your request. Please try again.
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default BackgroundFooter;
